import React from 'react';
import {
    LineChart as RechartsLineChart,
    Line,
    Area,
    AreaChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { colorUtils } from '../../theme';

export interface LineChartDataItem {
    name: string;
    [key: string]: any;
}

interface LineChartProps {
    data: LineChartDataItem[];
    title?: string;
    subtitle?: string;
    height?: number;
    variant?: 'line' | 'area';
    dataKeys: Array<{
        key: string;
        color?: string;
        label?: string;
    }>;
    showGrid?: boolean;
    showLegend?: boolean;
    showTooltip?: boolean;
    curved?: boolean;
    xAxisKey?: string;
}

// Default color palette matching PieChart - using OKLCH
const DEFAULT_COLORS = [
    'oklch(60% 0.22 291)', // purple - primary
    'oklch(70% 0.15 200)', // cyan - secondary
    'oklch(65% 0.18 150)', // green - success
    'oklch(75% 0.18 75)',  // orange - warning
    'oklch(62% 0.22 25)',  // red - critical
    'oklch(65% 0.18 250)', // blue - info
];

export const LineChart: React.FC<LineChartProps> = ({
    data,
    title,
    subtitle,
    height = 250,
    variant = 'line',
    dataKeys,
    showGrid = true,
    showLegend = true,
    showTooltip = true,
    curved = true,
    xAxisKey = 'name',
}) => {
    const theme = useTheme();

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) return null;

        return (
            <Paper
                elevation={8}
                sx={{
                    p: 1.5,
                    backgroundColor: colorUtils.withOpacity(theme.palette.background.paper, 0.95),
                    border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.3)}`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                    {label}
                </Typography>
                {payload.map((entry: any, index: number) => (
                    <Typography
                        key={`tooltip-${index}`}
                        variant="body2"
                        sx={{ color: entry.color }}
                    >
                        {entry.name}: {entry.value.toLocaleString()}
                    </Typography>
                ))}
            </Paper>
        );
    };

    // Custom legend
    const CustomLegend = ({ payload }: any) => {
        if (!showLegend) return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    mt: 2,
                }}
            >
                {payload.map((entry: any, index: number) => (
                    <Box
                        key={`legend-${index}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '2px',
                                backgroundColor: entry.color,
                            }}
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.85rem' }}
                        >
                            {entry.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    const ChartComponent = variant === 'area' ? AreaChart : RechartsLineChart;

    return (
        <Box sx={{ overflow: 'visible' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                {(title || subtitle) && (
                    <Box>
                        {title && (
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            <ResponsiveContainer width="100%" height={height + 40}>
                <ChartComponent
                    data={data}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                    {showGrid && (
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={colorUtils.withOpacity(theme.palette.text.secondary, 0.15)}
                            vertical={false}
                        />
                    )}
                    <XAxis
                        dataKey={xAxisKey}
                        stroke={theme.palette.text.secondary}
                        style={{
                            fontSize: '0.75rem',
                            fontFamily: theme.typography.fontFamily,
                        }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        stroke={theme.palette.text.secondary}
                        style={{
                            fontSize: '0.75rem',
                            fontFamily: theme.typography.fontFamily,
                        }}
                        axisLine={false}
                        tickLine={false}
                    />
                    {showTooltip && <Tooltip content={<CustomTooltip />} />}
                    {showLegend && <Legend content={<CustomLegend />} />}

                    {dataKeys.map((dataKey, index) => {
                        const color = dataKey.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];

                        if (variant === 'area') {
                            return (
                                <Area
                                    key={dataKey.key}
                                    type={curved ? 'monotone' : 'linear'}
                                    dataKey={dataKey.key}
                                    name={dataKey.label || dataKey.key}
                                    stroke={color}
                                    fill={colorUtils.withOpacity(color, 0.2)}
                                    strokeWidth={2}
                                    dot={{
                                        fill: color,
                                        strokeWidth: 0,
                                        r: 4,
                                    }}
                                    activeDot={{
                                        r: 6,
                                        fill: color,
                                        strokeWidth: 0,
                                    }}
                                />
                            );
                        }

                        return (
                            <Line
                                key={dataKey.key}
                                type={curved ? 'monotone' : 'linear'}
                                dataKey={dataKey.key}
                                name={dataKey.label || dataKey.key}
                                stroke={color}
                                strokeWidth={2}
                                dot={{
                                    fill: color,
                                    strokeWidth: 0,
                                    r: 4,
                                }}
                                activeDot={{
                                    r: 6,
                                    fill: color,
                                    strokeWidth: 0,
                                }}
                            />
                        );
                    })}
                </ChartComponent>
            </ResponsiveContainer>
        </Box>
    );
};

export default LineChart;
